<template>
    <div class="py-2 md:py-4 relative">
        <div
            class="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-5"
        >
            <div class="gap-2 flex flex-col">
                <h1 class="text-2xl font-semibold text-primary">Your Trips</h1>
                <p class="text-slate-500 dark:text-slate-400 text-sm">
                    View a history of all your point redemptions for travel
                </p>
            </div>
            <div class="flex gap-4 mt-4 md:mt-0">
                <button
                    class="btn-base btn-dark !mt-0 !px-4 flex items-center gap-2"
                    @click="openTripModal()"
                    id="hs-redemption-create-trip-open-modal"
                >
                    <Icon icon="plus" pathClass="stroke-[#F27249]" />
                    Create trip
                </button>
            </div>
        </div>
        <!-- Email Import Promo Banner — Free user -->
        <div
            v-if="
                showEmailImportBanner &&
                !loading &&
                !isSubscribed
            "
            class="flex flex-col sm:flex-row w-full items-start sm:items-center gap-3 sm:gap-8 p-4 rounded-lg border border-[#FEAF9F] dark:border-[#7c2d12] shadow-sm mb-4 relative"
            :style="emailImportBannerStyle"
        >
            <button
                class="absolute top-3 right-3 sm:hidden flex items-center justify-center p-1 rounded text-slate-400 hover:text-slate-600 dark:text-slate-300"
                @click="dismissEmailImportBanner"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M15 5L5 15M5 5L15 15"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </button>
            <div class="flex items-start gap-3 flex-1 min-w-0">
                <Icon
                    icon="email-forward"
                    class="shrink-0 text-[#FD421D]"
                    :width="20"
                    :height="20"
                    pathClass="text-[#FD421D]"
                />
                <div
                    class="flex flex-col justify-center items-start gap-1 flex-1"
                >
                    <p
                        class="text-base font-medium leading-6 text-[#1E293B] dark:text-slate-100 pr-6 sm:pr-0"
                    >
                        Import redemptions from email with 10xTravel+
                    </p>
                    <p class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400">
                        Forward your flight and hotel confirmation emails to add
                        them to trips automatically.
                    </p>
                </div>
            </div>
            <div class="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                <button
                    @click="showLearnMoreModal = true"
                    class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-sm font-medium text-[#1E293B] dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                    Learn more
                </button>
                <button
                    @click="showUpgradeModal = true"
                    class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded bg-[#FD421D] shadow-sm text-sm font-medium leading-5 text-white hover:bg-[#e53b19]"
                >
                    Upgrade
                </button>
            </div>
            <button
                class="hidden sm:flex shrink-0 items-center justify-center p-2 rounded text-slate-400 hover:text-slate-600 dark:text-slate-300"
                @click="dismissEmailImportBanner"
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M15 5L5 15M5 5L15 15"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </button>
        </div>

        <!-- Email Import Promo Banner — Paid user -->
        <div
            v-if="
                showEmailImportBanner &&
                !loading &&
                isSubscribed
            "
            class="flex flex-col sm:flex-row w-full items-start sm:items-center gap-3 sm:gap-8 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm mb-4 bg-white dark:bg-slate-800"
        >
            <div class="flex items-start gap-3 flex-1 min-w-0">
                <Icon
                    icon="email-forward"
                    class="shrink-0 text-[#FD421D]"
                    :width="20"
                    :height="20"
                    pathClass="text-[#FD421D]"
                />
                <div
                    class="flex flex-col justify-center items-start gap-1 flex-1"
                >
                    <p class="text-base font-medium leading-6 text-[#1E293B] dark:text-slate-100">
                        Import redemptions from email
                    </p>
                    <p class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400">
                        Forward your flight and hotel confirmation emails to
                        redemptions@10xtravel.com to add them to trips
                        automatically.
                    </p>
                </div>
            </div>
            <div class="flex items-center gap-2 shrink-0 w-full sm:w-auto">
                <button
                    class="text-sm text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 px-4 py-2 flex-1 sm:flex-none"
                    @click="dismissEmailImportBanner"
                >
                    Dismiss
                </button>
                <button
                    @click="showLearnMoreModal = true"
                    class="flex items-center justify-center flex-1 sm:flex-none px-4 py-2 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-sm font-medium text-[#1E293B] dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                    Learn more
                </button>
            </div>
        </div>

        <ArchiveStats />

        <div class="mb-6 md:mb-8">
            <div class="flex items-center justify-between">
                <div class="inline-flex items-center gap-4">
                    <label
                        class="py-4 text-sm font-medium border-b-2 cursor-pointer transition-colors duration-300 relative z-10"
                        :class="{
                            'border-orange-500 text-slate-800 dark:text-slate-100':
                                tripScheduled === 'upcoming',
                            'border-transparent text-slate-600 dark:text-slate-300':
                                tripScheduled !== 'upcoming',
                        }"
                    >
                        <span>Upcoming</span>
                        <input
                            class="sr-only"
                            type="radio"
                            name="trip_scheduled"
                            value="upcoming"
                            v-model="tripScheduled"
                        />
                    </label>
                    <label
                        class="py-4 text-sm font-medium border-b-2 cursor-pointer transition-colors duration-300 relative z-10"
                        :class="{
                            'border-orange-500 text-slate-800 dark:text-slate-100':
                                tripScheduled === 'past',
                            'border-transparent text-slate-600 dark:text-slate-300':
                                tripScheduled !== 'past',
                        }"
                    >
                        <span>Past</span>
                        <input
                            class="sr-only"
                            type="radio"
                            name="trip_scheduled"
                            value="past"
                            v-model="tripScheduled"
                        />
                    </label>
                </div>
                <div class="flex items-center gap-4">
                    <button
                        v-if="isSubscribed"
                        @click="showLearnMoreModal = true"
                        class="hidden sm:flex items-center gap-1.5 text-sm font-medium text-left text-[#1E293B] dark:text-slate-100 hover:text-slate-600 dark:text-slate-300 rounded"
                    >
                        <Icon
                            icon="email-forward"
                            class="hidden sm:block"
                            :width="16"
                            :height="16"
                            pathClass="text-slate-800 dark:text-slate-100"
                        />
                        Import from email
                    </button>
                    <button
                        v-else
                        @click="showLearnMoreModal = true"
                        class="flex items-center gap-1.5 text-sm font-medium text-left text-[#1E293B] dark:text-slate-100 hover:text-slate-600 dark:text-slate-300 rounded"
                    >
                        <Icon
                            icon="email-forward"
                            class="hidden sm:block"
                            :width="16"
                            :height="16"
                            pathClass="text-slate-800 dark:text-slate-100"
                        />
                        Upgrade to import from email
                    </button>
                    <Link
                        v-if="unfiledCount > 0"
                        href="/trips/unfiled"
                        class="flex items-center gap-1 text-left cursor-pointer"
                    >
                        <Icon
                            icon="email-forward"
                            :width="16"
                            :height="16"
                            class="hidden sm:block text-slate-800 dark:text-slate-100"
                        />
                        <span
                            class="text-sm font-medium text-slate-800 dark:text-slate-100 leading-5 rounded"
                        >
                            Unfiled redemptions
                        </span>
                        <span
                            class="inline-flex min-w-[20px] px-1.5 py-[2px] items-center justify-center rounded-full bg-[#FD421D] text-white text-xs font-medium leading-4"
                        >
                            {{ unfiledCount }}
                        </span>
                    </Link>
                </div>
            </div>
            <hr class="border-t-2 border-slate-200 dark:border-slate-700 w-full relative -mt-[2px]" />
        </div>

        <div class="flex flex-col gap-4" v-if="tripScheduled === 'upcoming'">
            <TripCard
                v-for="trip in upcomingTrips"
                :key="trip.id"
                :trip="trip"
            />
            <div class="w-full pt-5">
                <Pagination
                    :current-page="upcomingTripsPage"
                    :total-pages="upcomingTripsPages"
                    :per-page="perPage"
                    :total-results="upcomingTripsTotal"
                    :max-visible-buttons="5"
                    @page-changed="handleUpcomingPageChange"
                    v-if="upcomingTrips.length > 0"
                />
            </div>
            <EmptyState
                v-if="upcomingTrips.length === 0 && showEmptyTripState"
                class="mb-10"
                :icon="'trip'"
                title="No upcoming trips yet"
                description="Create a new one using the button below."
            >
                <template #action>
                    <button
                        class="btn-base btn-dark gap-2 flex"
                        @click="openTripModal()"
                        id="hs-redemption-create-trip-empty"
                    >
                        <Icon icon="plus" pathClass="stroke-[#F27249]" />
                        Create a trip
                    </button>
                </template>
            </EmptyState>
        </div>

        <div class="flex flex-col gap-4" v-if="tripScheduled === 'past'">
            <TripCard v-for="trip in pastTrips" :key="trip.id" :trip="trip" />
            <div class="w-full pt-5">
                <Pagination
                    :current-page="pastTripsPage"
                    :total-pages="pastTripsPages"
                    :per-page="perPage"
                    :total-results="pastTripsTotal"
                    :max-visible-buttons="5"
                    @page-changed="handlePastPageChange"
                    v-if="pastTrips.length > 0"
                />
            </div>
            <EmptyState
                v-if="pastTrips.length === 0"
                class="mb-10"
                :icon="'trip'"
                title="No past trips yet"
                description="As soon as your trips finish, you will see them here."
            >
                <template #action>
                    <button
                        class="btn-base btn-dark gap-2 flex"
                        @click="openTripModal()"
                        id="hs-redemption-create-trip-past-empty"
                    >
                        <Icon icon="plus" pathClass="stroke-[#F27249]" />
                        Create a trip
                    </button>
                </template>
            </EmptyState>
        </div>

        <ManageTrip />
        <CreateRedemption v-if="showRedemptionCreateModal" />
        <EditRedemption v-if="showRedemptionEditModal" />

        <Teleport to="body">
            <!-- Email Import Learn More Modal -->
            <EmailImportModal
                :show="showLearnMoreModal"
                :isSubscribed="isSubscribed"
                :userEmail="userData?.email || 'your email'"
                @close="showLearnMoreModal = false"
            />

            <!-- 10xTravel+ Upgrade Modal -->
            <UpgradeModal
                :show="showUpgradeModal"
                description="This feature is part of the 10xTravel+ plan. Upgrade to unlock it and get full access."
                @close="showUpgradeModal = false"
            />
        </Teleport>
    </div>
    <LoadingState :loading="loading" />
</template>
<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Link, usePage } from '@inertiajs/vue3';
import { useAppStore } from '@/store/app';
import { useTripsStore } from '@/store/trips';
import { useThemeStore } from '@/store/theme';
import ArchiveStats from '@/components/trips/archive-stats.vue';
import TripCard from '@/components/trips/trip-card.vue';
import ManageTrip from '@/components/trips/modals/manage-trip.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import Icon from '@/components/ui/icon/icon.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import Pagination from '@/components/ui/pagination/pagination.vue';
import CreateRedemption from '@/components/trips/modals/create-redemption.vue';
import EditRedemption from '@/components/trips/modals/edit-redemption.vue';
import EmailImportModal from '@/components/trips/modals/email-import-modal.vue';
import UpgradeModal from '@/modals/upgrade-modal.vue';
import { Apii } from '@/services/api';

const ApiService = new Apii();
const tripsStore = useTripsStore();
const appStore = useAppStore();
const themeStore = useThemeStore();
const {
    loading,
    showEmptyTripState,
    upcomingTrips,
    pastTrips,
    perPage,
    upcomingTripsPage,
    pastTripsPage,
    upcomingTripsPages,
    pastTripsPages,
    upcomingTripsTotal,
    pastTripsTotal,
    showRedemptionCreateModal,
    showRedemptionEditModal,
} = storeToRefs(tripsStore);

const userData = computed(() => usePage().props.userData);
const isSubscribed = computed(() => userData.value?.is_subscribed ?? false);
const bannerDismissed = ref(false);
const showLearnMoreModal = ref(false);
const showUpgradeModal = ref(false);

const showEmailImportBanner = computed(() => {
    return (
        !bannerDismissed.value && !userData.value?.email_import_banner_dismissed
    );
});

const emailImportBannerStyle = computed(() => {
    const baseColor = themeStore.isDark ? '#1e293b' : '#fff';
    return `background: linear-gradient(66deg, rgba(253, 96, 29, 0) 25.49%, rgba(253, 96, 29, 0.12) 77.35%, rgba(253, 96, 29, 0) 112.21%), ${baseColor};`;
});

const dismissEmailImportBanner = async () => {
    bannerDismissed.value = true;
    try {
        await ApiService.apiCall('/user/user-setting', 'put', {
            key: 'email_import_banner_dismissed',
            value: true,
        });
    } catch (error) {
        // Silently fail — banner stays hidden for this session
    }
};

const tripScheduled = ref('upcoming');
const unfiledCount = ref(0);

const handleUpcomingPageChange = (page) => {
    tripsStore.tripsPageChange(page, 'upcoming');
};

const handlePastPageChange = (page) => {
    tripsStore.tripsPageChange(page, 'past');
};

const openTripModal = () => {
    document.activeElement?.blur();
    tripsStore.resetActiveTripData();
    tripsStore.setShowTripModal(true);
};

const fetchUnfiledCount = async () => {
    try {
        const response = await ApiService.apiCall(
            '/redemptions/unfiled?page=1',
            'get'
        );
        const data = response.data || response;
        unfiledCount.value = data.total || 0;
    } catch {
        unfiledCount.value = 0;
    }
};

// Watch for new trips being created to attach pending redemption
watch(
    () => [...upcomingTrips.value, ...pastTrips.value].length,
    async (newLength, oldLength) => {
        if (!tripsStore.pendingAttachRedemptionId) return;

        // Only proceed if a new trip was added (length increased)
        if (newLength <= oldLength) return;

        // Find the newest trip
        const allTrips = [...upcomingTrips.value, ...pastTrips.value];
        const newTrip = allTrips[allTrips.length - 1];

        if (newTrip) {
            const redemptionId = tripsStore.pendingAttachRedemptionId;
            tripsStore.setPendingAttachRedemptionId(null); // Clear immediately

            try {
                await ApiService.apiCall(
                    `/redemptions/${redemptionId}/attach-to-trip`,
                    'post',
                    { trip_id: newTrip.id }
                );
                tripsStore.getTrips(); // Refresh trips
            } catch (err) {
                console.error('Failed to attach redemption to new trip:', err);
            }
        }
    }
);

onMounted(() => {
    tripsStore.getTrips();
    fetchUnfiledCount();
});
</script>
